/**
 * Loot tables / rarity / affix configuration (M7-B).
 *
 * Values marked DEV_PROVISIONAL are NOT final design balance — replace when
 * loot curves are designed. Generator reads only this config (no Math.random).
 *
 * Locked rarities (M7-B brief): Common, Uncommon, Rare, Epic, Legendary.
 * Older GDD listed Mythic instead of Uncommon — Mythic is deferred / not rolled.
 */

import type { ItemCategory, ItemRarity } from './equipment';

/** Locked M7-B rarity tiers (generation order low → high). */
export const LOOT_RARITIES = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'legendary',
] as const;

export type LootRarity = (typeof LOOT_RARITIES)[number];

export function isLootRarity(value: string): value is LootRarity {
  return (LOOT_RARITIES as readonly string[]).includes(value);
}

/** Dungeon difficulty ids used for boss-chest scaling (loot only, never XP). */
export const DUNGEON_DIFFICULTIES = ['normal', 'hard', 'brutal'] as const;
export type DungeonDifficultyId = (typeof DUNGEON_DIFFICULTIES)[number];

export function isDungeonDifficultyId(value: string): value is DungeonDifficultyId {
  return (DUNGEON_DIFFICULTIES as readonly string[]).includes(value);
}

/**
 * Affix stat kinds persisted on instances.
 * Magnitudes are applied in combat via equipmentCombat → characterStats (M7-C).
 */
export type AffixStatKind =
  | 'flat_armour'
  | 'flat_max_hp'
  | 'attack_damage_pct'
  | 'crit_chance_pp'
  | 'block_chance_pp'
  | 'dodge_chance_pp';

export interface AffixDef {
  id: string;
  name: string;
  stat: AffixStatKind;
  /** Eligible item categories. */
  categories: readonly ItemCategory[];
  /**
   * DEV_PROVISIONAL — inclusive integer roll range for affix value.
   * Not final balance.
   */
  valueMin: number;
  valueMax: number;
}

/** Rolled affix on an item instance. */
export interface AffixRoll {
  affixId: string;
  /** Rolled numeric magnitude (persisted; combat deferred). */
  value: number;
}

/**
 * How many affixes to roll per rarity.
 * DEV_PROVISIONAL — undocumented in design docs; replace when designed.
 */
export const AFFIX_COUNT_BY_RARITY: Record<LootRarity, number> = {
  common: 0,
  uncommon: 1,
  rare: 2,
  epic: 3,
  legendary: 4,
};

/**
 * Affix catalog — structural only; values are DEV_PROVISIONAL placeholders.
 * No legendary unique effects invented.
 */
export const AFFIX_DEFS: readonly AffixDef[] = [
  {
    id: 'aff_flat_armour',
    name: 'Armour',
    stat: 'flat_armour',
    categories: ['armour', 'accessory'],
    valueMin: 1,
    valueMax: 4,
  },
  {
    id: 'aff_flat_max_hp',
    name: 'Max HP',
    stat: 'flat_max_hp',
    categories: ['armour', 'accessory', 'special_die'],
    valueMin: 2,
    valueMax: 8,
  },
  {
    id: 'aff_attack_pct',
    name: 'Attack Damage %',
    stat: 'attack_damage_pct',
    categories: ['weapon', 'accessory', 'special_die'],
    valueMin: 2,
    valueMax: 10,
  },
  {
    id: 'aff_crit_chance',
    name: 'Crit Chance',
    stat: 'crit_chance_pp',
    categories: ['weapon', 'accessory'],
    valueMin: 1,
    valueMax: 5,
  },
  {
    id: 'aff_block_chance',
    name: 'Block Chance',
    stat: 'block_chance_pp',
    categories: ['armour', 'weapon'],
    valueMin: 1,
    valueMax: 5,
  },
  {
    id: 'aff_dodge_chance',
    name: 'Dodge Chance',
    stat: 'dodge_chance_pp',
    categories: ['armour', 'accessory'],
    valueMin: 1,
    valueMax: 5,
  },
] as const;

const AFFIX_BY_ID = new Map(AFFIX_DEFS.map((a) => [a.id, a]));

export function getAffixDef(id: string): AffixDef | undefined {
  return AFFIX_BY_ID.get(id);
}

/** Category weights when picking a base item family. DEV_PROVISIONAL. */
export const CATEGORY_WEIGHTS: ReadonlyArray<{ category: ItemCategory; weight: number }> = [
  { category: 'weapon', weight: 35 },
  { category: 'armour', weight: 40 },
  { category: 'accessory', weight: 20 },
  { category: 'special_die', weight: 5 },
];

/**
 * Rarity weight tables by difficulty (higher difficulty → better loot).
 * DEV_PROVISIONAL — no guaranteed rarity floor (matches GDD intent).
 */
export const RARITY_WEIGHTS_BY_DIFFICULTY: Record<
  DungeonDifficultyId,
  ReadonlyArray<{ rarity: LootRarity; weight: number }>
> = {
  normal: [
    { rarity: 'common', weight: 55 },
    { rarity: 'uncommon', weight: 28 },
    { rarity: 'rare', weight: 12 },
    { rarity: 'epic', weight: 4 },
    { rarity: 'legendary', weight: 1 },
  ],
  hard: [
    { rarity: 'common', weight: 35 },
    { rarity: 'uncommon', weight: 32 },
    { rarity: 'rare', weight: 20 },
    { rarity: 'epic', weight: 10 },
    { rarity: 'legendary', weight: 3 },
  ],
  brutal: [
    { rarity: 'common', weight: 20 },
    { rarity: 'uncommon', weight: 30 },
    { rarity: 'rare', weight: 28 },
    { rarity: 'epic', weight: 16 },
    { rarity: 'legendary', weight: 6 },
  ],
};

/**
 * Equipment drop chance after defeating an eligible enemy (0–1).
 * DEV_PROVISIONAL — undocumented; ordinary enemies are hard-zero elsewhere.
 */
export const EQUIPMENT_DROP_CHANCE: {
  elite: number;
  /** On boss kill (separate from boss chest). */
  bossKill: number;
} = {
  elite: 0.35,
  bossKill: 0.55,
};

/**
 * Boss chest equipment roll counts by difficulty.
 * DEV_PROVISIONAL — GDD says “multiple” items, no exact counts.
 */
export const BOSS_CHEST_ITEM_COUNTS: Record<DungeonDifficultyId, number> = {
  normal: 2,
  hard: 3,
  brutal: 4,
};

/**
 * Item level band used when rolling (cosmetic/persisted; combat deferred).
 * DEV_PROVISIONAL.
 */
export const ITEM_LEVEL_BY_DIFFICULTY: Record<
  DungeonDifficultyId,
  { min: number; max: number }
> = {
  normal: { min: 1, max: 3 },
  hard: { min: 2, max: 5 },
  brutal: { min: 4, max: 8 },
};

export function asItemRarity(rarity: LootRarity): ItemRarity {
  return rarity;
}
